// --- Superpowers ---
export const SUPERPOWERS = [
  'Empathy',
  'Strategy',
  'Resilience',
  'Curiosity',
  'Creativity',
  'Connection',
  'Leadership',
  'Courage',
] as const;

export type Superpower = (typeof SUPERPOWERS)[number];

// --- Feelings ---
export const FEELINGS = [
  'Energized',
  'Balanced',
  'Inspired',
  'Unstoppable',
  'Connected',
  'Thriving',
  'Grounded',
  'Purposeful',
] as const;

export type Feeling = (typeof FEELINGS)[number];

// --- Life Area Categories ---
export const LIFE_AREA_CATEGORIES = [
  'Lifestyle & Health',
  'Mindset, Mental Health & Resilience',
  'Growth & Development',
  'Financial Wellbeing & Total Rewards',
  'Purpose & Contribution',
  'Relationships & Community',
  'Something Else',
] as const;

export type LifeAreaCategory = (typeof LIFE_AREA_CATEGORIES)[number];

/** Display-friendly overrides for life area categories (when different from the stored value). */
export const LIFE_AREA_DISPLAY_NAMES: Partial<Record<LifeAreaCategory, string>> = {
  'Mindset, Mental Health & Resilience': 'Mindset & Resilience',
  'Financial Wellbeing & Total Rewards': 'Money & Prosperity',
};

export interface LifeArea {
  category: LifeAreaCategory;
  confidence: 'high' | 'medium' | 'low';
  rationale: string;
}

// --- Audiences ---
export const AUDIENCES = ['conference_attendees', 'clients', 'broker_partners', 'members'] as const;

export type Audience = (typeof AUDIENCES)[number];

// --- Surface ---
export interface Surface {
  id: string; // UUID
  name: string;
  description?: string;
  audience: Audience;
  active: boolean;
  submissionsEnabled: boolean;
  referralsEnabled?: boolean; // per-surface toggle for accepting referral submissions
  referralCap?: number; // max submissions per referral code (default: 10)
  eventHashtag?: string; // e.g. "#ConferenceBoard2026" — used in social share template
  deleted: boolean;
  deletedAt?: string; // ISO 8601
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

export interface CreateSurfaceRequest {
  name: string;
  description?: string;
  audience: Audience;
}

export interface UpdateSurfaceRequest {
  name?: string;
  description?: string;
  audience?: Audience;
  active?: boolean;
  submissionsEnabled?: boolean;
  referralsEnabled?: boolean;
  referralCap?: number;
  eventHashtag?: string;
}

export interface SurfaceListResponse {
  surfaces: Surface[];
}

// --- Submission ---
export type SubmissionStatus = 'generating' | 'complete' | 'failed';
export type PrintStatus = 'pending' | 'printing' | 'printed' | 'failed';

export interface Submission {
  id: string; // UUID — used for flame retrieval link
  surfaceTag: string; // UUID — the surface this submission belongs to
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  organization: string;
  freeText: string; // "What change do you want to ignite?" (max 200 chars)
  superpower: Superpower;
  feeling: Feeling;
  status: SubmissionStatus;
  flameImageUrl?: string;
  flameImageKey?: string; // S3 key
  flameAssetKey?: string; // S3 key for personalized flame card
  flameAssetUrl?: string; // constructed at read time from flameAssetKey
  referralCode?: string; // UUID — unique referral code for this submission
  referralQrKey?: string; // S3 key: flames/{submissionId}-referral.png
  referralQrUrl?: string; // constructed at read time
  referralQrStatus?: 'complete' | 'failed'; // set by generation pipeline
  referredBy?: string; // referralCode of the person who referred this visitor
  referredByName?: string; // display name of the referrer (resolved at read time)
  createdAt: string; // ISO 8601
  moderationFlagged?: boolean;
  moderationReason?: string; // human-readable reason from GPT moderation
  hidden?: boolean; // admin can hide from wall
  deleted?: boolean; // soft delete — hidden from wall, visible in admin
  printStatus?: PrintStatus;
  printRequestedAt?: string; // ISO timestamp — when enqueued (or re-enqueued)
  printedAt?: string; // ISO timestamp — when print agent confirmed
  raffleWinner?: boolean; // true when drawn as a raffle winner
  raffleWonAt?: string; // ISO timestamp of when they won
  lifeArea: LifeArea; // classified life-area category
  surfaceName?: string; // resolved from surfaceTag at read time
  surfaceEventHashtag?: string; // resolved from surface.eventHashtag at read time
  sourceIp?: string; // client IP from API Gateway (forensic metadata)
  userAgent?: string; // client User-Agent header (forensic metadata)
  referer?: string; // client Referer header (forensic metadata)
}

// --- Flame (public-facing, no PII) ---
export interface Flame {
  id: string;
  surfaceTag: string;
  displayName: string;
  organization: string;
  superpower: Superpower;
  feeling: Feeling;
  lifeArea: LifeAreaCategory;
  flameImageUrl: string;
  createdAt: string;
}

// --- API Request/Response types ---
export interface CreateSubmissionRequest {
  surfaceTag: string; // UUID — the surface to submit to
  firstName: string;
  lastName: string;
  displayName?: string; // defaults to firstName if blank
  email: string;
  organization: string;
  freeText: string;
  superpower: Superpower;
  feeling: Feeling;
  referredBy?: string; // referral code of the person who shared the link
}

export interface CreateSubmissionResponse {
  id: string;
  status: SubmissionStatus;
}

export interface FlameListResponse {
  flames: Flame[];
  totalCount: number;
  submissionsOpen: boolean;
}

export interface SubmissionsCheckResponse {
  accepting: boolean;
  message?: string;
}

export interface StatsResponse {
  firesLit: number;
}

// --- WebSocket events ---
export interface WsNewFlameEvent {
  type: 'new-flame';
  flame: Flame;
  totalCount: number;
}

export type WsEvent = WsNewFlameEvent;

// --- Config ---
export interface ConfigEntry {
  key: string;
  enabled: boolean;
  value?: number;
  maxValue?: number;
  updatedAt: string;
}
